import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  ArchiveIcon,
  ArchiveRestoreIcon,
  MapPinIcon,
  MoreHorizontalIcon,
  PencilIcon,
  ShoppingBagIcon,
  ShoppingCartIcon,
  Trash2Icon,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { DocumentList, ImageGallery } from "@/components/app/attachments";
import { ChassisCodeChip } from "@/components/app/chassis-codes";
import { LinksPanel } from "@/components/app/links";
import { Markdown } from "@/components/app/markdown";
import { Page, PageHeader, SectionTitle } from "@/components/app/page";
import { PartOriginBadge } from "@/components/app/part-origin";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/misc";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PartLabelsCard } from "@/features/labels/part-labels";
import { AddToOrderDialog } from "@/features/orders/add-to-order-dialog";
import { QuickPurchaseDialog } from "@/features/orders/quick-purchase-dialog";
import { History } from "@/features/parts/history";
import { LinkedPartsCard } from "@/features/parts/linked-parts";
import { NumbersCard } from "@/features/parts/numbers-card";
import { OrderLines } from "@/features/parts/order-lines";
import { PartSheet, TagDot } from "@/features/parts/part-sheet";
import { SourcesSection } from "@/features/parts/sources";
import { StockCard } from "@/features/parts/stock-card";
import { UsageList } from "@/features/parts/usage";
import { errorCode } from "@/lib/errors";
import { useTRPC } from "@/lib/trpc";

export const Route = createFileRoute("/inventory/$partId")({
  component: PartPage,
});

function PartPage() {
  const { partId } = Route.useParams();
  const { t } = useTranslation();
  const trpc = useTRPC();
  const navigate = useNavigate();
  const { data: part, isLoading } = useQuery(trpc.parts.get.queryOptions({ id: partId }));
  const [editing, setEditing] = useState(false);
  const [ordering, setOrdering] = useState(false);
  const [buying, setBuying] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const archive = useMutation(trpc.parts.setArchived.mutationOptions());
  const remove = useMutation({ ...trpc.parts.delete.mutationOptions(), meta: { silent: true } });

  if (isLoading || !part) {
    return (
      <Page>
        <Skeleton className="mb-6 h-12 w-80" />
        <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
          <Skeleton className="aspect-[4/3] rounded-xl" />
          <Skeleton className="h-64 rounded-xl" />
        </div>
      </Page>
    );
  }

  const primary = part.numbers.find((n) => n.kind === "primary");
  const suggested = Math.max(part.figures.toOrder - part.figures.inDraft, 0) || 1000;

  return (
    <Page>
      <PageHeader
        crumbs={[{ label: t("nav.inventory"), to: "/inventory" }]}
        title={part.name}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {primary ? (
              <span className="font-mono text-foreground/80">{primary.number}</span>
            ) : null}
            {part.manufacturer ? <span>{part.manufacturer}</span> : null}
            <PartOriginBadge origin={part.origin} />
            {part.location ? (
              <span className="flex items-center gap-1">
                <MapPinIcon className="size-3.5" />
                {part.location.path.join(" › ")}
                {part.locationDetail ? ` · ${part.locationDetail}` : ""}
              </span>
            ) : null}
          </span>
        }
        actions={
          <>
            <Button variant="outline" onClick={() => setEditing(true)}>
              <PencilIcon />
              {t("common.edit")}
            </Button>
            <Button onClick={() => setOrdering(true)}>
              <ShoppingCartIcon />
              {t("parts.addToOrder")}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label={t("common.more")}>
                  <MoreHorizontalIcon />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setBuying(true)}>
                  <ShoppingBagIcon />
                  {t("orders.quickPurchase")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() =>
                    archive.mutate(
                      { id: part.id, archived: !part.archived },
                      {
                        onSuccess: () =>
                          toast.success(t(part.archived ? "parts.restored" : "parts.archived")),
                      },
                    )
                  }
                >
                  {part.archived ? <ArchiveRestoreIcon /> : <ArchiveIcon />}
                  {part.archived ? t("common.unarchive") : t("common.archive")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem destructive onSelect={() => setConfirmDelete(true)}>
                  <Trash2Icon />
                  {t("common.delete")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      />

      {part.archived ? (
        <div className="mb-5 flex items-center gap-2 rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">
          <ArchiveIcon className="size-4" />
          {t("parts.archivedBanner")}
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        <aside className="flex flex-col gap-5">
          <ImageGallery ownerType="part" ownerId={part.id} attachments={part.attachments} />
          <NumbersCard part={part} />
          <LinkedPartsCard part={part} />
          <PartLabelsCard partId={part.id} partName={part.name} />
          {part.tags.length ? (
            <div className="flex flex-wrap gap-1.5">
              {part.tags.map((tag) => (
                <Link key={tag.id} to="/inventory" search={{ tag: tag.id }}>
                  <Badge tone="outline" className="gap-1.5 py-1 text-foreground hover:bg-muted">
                    <TagDot color={tag.color} />
                    {tag.name}
                  </Badge>
                </Link>
              ))}
            </div>
          ) : null}
          {part.chassisCodes.length ? (
            <div>
              <div className="mb-1.5 text-xs font-medium text-muted-foreground">
                {t("chassisCodes.title")}
              </div>
              <ul className="flex flex-wrap gap-1.5">
                {part.chassisCodes.map((code) => (
                  <li key={code.id}>
                    <Link to="/inventory" search={{ chassis: code.id }}>
                      <ChassisCodeChip
                        code={code}
                        className="px-1.5 py-0.5 text-xs hover:brightness-95 dark:hover:brightness-125"
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </aside>

        <div className="flex min-w-0 flex-col gap-6">
          <StockCard part={part} />
          <Tabs defaultValue="details">
            <TabsList>
              <TabsTrigger value="details">{t("common.details")}</TabsTrigger>
              <TabsTrigger value="usage">
                {t("parts.whereUsed")}
                <Count n={part.usage.length + part.templates.length} />
              </TabsTrigger>
              <TabsTrigger value="orders">
                {t("parts.orders")}
                <Count n={part.orderLines.length} />
              </TabsTrigger>
              <TabsTrigger value="history">{t("parts.history")}</TabsTrigger>
            </TabsList>
            <TabsContent value="details" className="flex flex-col gap-7">
              <SourcesSection part={part} />
              <section>
                <SectionTitle>{t("common.description")}</SectionTitle>
                {part.description ? (
                  <Markdown>{part.description}</Markdown>
                ) : (
                  <p className="text-sm text-muted-foreground">{t("parts.noDescription")}</p>
                )}
                {part.notes ? (
                  <div className="mt-4 rounded-lg bg-muted/60 px-3 py-2 text-sm whitespace-pre-wrap">
                    {part.notes}
                  </div>
                ) : null}
              </section>
              <div className="grid gap-7 md:grid-cols-2">
                <section>
                  <SectionTitle>{t("common.links")}</SectionTitle>
                  <LinksPanel ownerType="part" ownerId={part.id} links={part.links} />
                </section>
                <section>
                  <SectionTitle>{t("common.documents")}</SectionTitle>
                  <DocumentList ownerType="part" ownerId={part.id} attachments={part.attachments} />
                </section>
              </div>
            </TabsContent>
            <TabsContent value="usage">
              <UsageList part={part} />
            </TabsContent>
            <TabsContent value="orders">
              <OrderLines part={part} />
            </TabsContent>
            <TabsContent value="history">
              <History part={part} />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <PartSheet open={editing} onOpenChange={setEditing} part={part} />
      <AddToOrderDialog
        open={ordering}
        onOpenChange={setOrdering}
        items={[
          {
            partId: part.id,
            name: part.name,
            unit: part.unit,
            qty: suggested,
            workOrderId: null,
            sources: part.sources,
          },
        ]}
      />
      <QuickPurchaseDialog
        open={buying}
        onOpenChange={setBuying}
        initialPart={{ id: part.id, name: part.name, unit: part.unit }}
      />
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t("common.deleteConfirmTitle", { name: part.name })}
        confirmLabel={t("common.delete")}
        destructive
        pending={remove.isPending}
        onConfirm={() =>
          remove.mutate(
            { id: part.id },
            {
              onSuccess: () => {
                toast.success(t("parts.deleted"));
                void navigate({ to: "/inventory" });
              },
              onError: (error) => {
                setConfirmDelete(false);
                toast.error(
                  errorCode(error) === "PART_IN_USE"
                    ? t("parts.deleteBlocked")
                    : t("errors.generic"),
                );
              },
            },
          )
        }
      />
    </Page>
  );
}

function Count({ n }: { n: number }) {
  return n > 0 ? (
    <span className="rounded-full bg-muted px-1.5 text-[11px] text-muted-foreground tabular">
      {n}
    </span>
  ) : null;
}
